<template>
  <sticky-header-layout
    v-loading="isPageLoading"
  >
    <template #header>
      <div class="filter-container">
        <div class="filter-item">
          <span class="filter-title">Client ID:</span>
          <el-select
            :placeholder="$t('placeholder.select')"
            :disabled="isPageLoading"
            v-model="privateClientID"
            filterable
          >
            <el-option
              v-for="item in clientList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
        <el-button
          :loading="isPageLoading || isLoading"
          class="ml-2"
          size="mini"
          type="primary"
          icon="el-icon-search"
          circle
          @click="handleSearch"
        />
      </div>
      <div class="action-container">
        <div class="pagination-container">
          <el-pagination
            slot="left"
            :disabled="isLoading"
            :current-page="listQuery.page"
            :page-sizes="listQuery.sizes"
            :page-size="listQuery.size"
            :total="listQuery.total"
            background
            layout="total, sizes, pager, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
        <div class="button-container">
          <el-button
            class="add-button"
            type="primary"
            icon="el-icon-edit"
            @click="handleClickCreateButton"
          >{{ $t('button.create') }}</el-button>
        </div>
      </div>
    </template>

    <el-table
      v-loading="isLoading"
      :data="list"
      border
      stripe
    >
      <el-table-column
        prop="order_number"
        label="Order Number"
        width="150"
      />

      <el-table-column
        label="Shopper"
        prop="shopper_name"
      />

      <el-table-column
        label="Client"
        prop="customer_name"
      />

      <el-table-column
        label="Currency"
        prop="currency"
        width="80"
      />

      <el-table-column
        label="Total"
        prop="total"
        width="100"
      />

      <el-table-column
        label="Refunded Total"
        prop="refunded_amount"
        width="140"
      />

      <el-table-column
        label="Status"
        prop="payment_status_description"
        width="140"
      />

      <el-table-column
        label="Order Qty"
        prop="order_quantity"
        width="100"
      />

      <el-table-column
        label="Purchase Qty"
        prop="purchase_quantity"
        width="100"
      />

      <el-table-column
        label="Tracking"
        prop="tracking_number"
        width="120"
      />

      <el-table-column
        label="Link Expired At"
        prop="expired_at"
        width="150"
      />

      <el-table-column
        :label="$t('table.action')"
        fixed="right"
        align="center"
        width="120"
      >
        <template #default="{ row }">
          <div class="button-group">
            <el-button
              size="mini"
              type="info"
              icon="el-icon-view"
              circle
              @click="showOrderDetail(row)"
            />
            <el-button
              size="mini"
              type="warning"
              icon="el-icon-edit"
              circle
              @click="handleClickEditButton(row)"
            />

            <el-button
              v-if="(row.can_refund)"
              size="mini"
              type="danger"
              icon="el-icon-back"
              circle
              @click="showRefundDetail(row)"
            />
          </div>
        </template>
      </el-table-column>
    </el-table>
    
    <el-dialog
      v-if="showOrderDetailModal"
      :visible.sync="showOrderDetailModal"
      title="Order Detail"
    >
      <el-form
        class="round-border-form"
        label-position="left"
        label-width="150px"
      >
        <el-form-item label="Pay Link">
          {{ getPayLink(selectedOrder.hash) }} 
          <el-button
            v-clipboard:copy="getPayLink(selectedOrder.hash)"
            v-clipboard:success="handleCopySuccess"
            icon="el-icon-tickets"
            type="primary"
            size="mini"
          >
            Copy
          </el-button>
        </el-form-item>
        <el-form-item label="Order Number">
          {{ selectedOrder.order_number }}
        </el-form-item>
        <el-form-item label="Client">
          {{ selectedOrder.client.name }}
        </el-form-item>
        <el-form-item label="Delivery Address">
          {{ selectedOrder.delivery_address }}
        </el-form-item>
        <el-form-item label="Currency">
          {{ selectedOrder.currency }}
        </el-form-item>
        <el-form-item label="Shipping Fee">
          {{ selectedOrder.shipping_fee }}
        </el-form-item>
        <el-form-item label="Total">
          {{ selectedOrder.total }}
        </el-form-item>
        <el-form-item label="Refunded Total">
          {{ selectedOrder.refunded_amount }}
        </el-form-item>
      </el-form>

      <el-table
        style="margin-top: 20px;" 
        :data="selectedOrder.items"
      >
        <el-table-column
          prop="brand_name"
          label="Brand Name"
          :disabled="isPaid"
        />
        <el-table-column
          prop="product_name"
          label="Product Name"
          :disabled="isPaid"
        />
        <el-table-column
          prop="size"
          label="Size"
          :disabled="isPaid"
        />
        <el-table-column
          prop="remarks"
          label="Remarks"
          :disabled="isPaid"
        />
        <el-table-column
          prop="quantity"
          label="Quantity"
          width="80"
          :disabled="isPaid"
        />
        <el-table-column
          prop="refunded_qty"
          label="Refunded Qty"
          width="100"
          :disabled="isPaid"
        />
        <el-table-column
          prop="currency"
          label="Currency"
          width="80"
        />
        <el-table-column
          prop="unit_price"
          label="Unit Price"
          width="100"
          :disabled="isPaid"
        />
      </el-table>
    </el-dialog>
    <el-dialog
      :visible.sync="showRefundModal"
      title="Refund"
      @open="resetRefundParam"
      @closed="handleRefundModalClosed"
    >
      <div>
        <el-switch 
          v-model="isFullRefund"
          :disabled="!canFullRefund"
        />
        Full Refund
      </div>

      <div style="margin-top: 20px;" >
        Refund to
        <div>
          <el-radio v-model="refundTo" label="CREDIT_CARD" border>Credit Card</el-radio>
          <el-radio v-model="refundTo" label="CREDIT" border>Credit</el-radio>
        </div>
      </div>

      <el-table
        style="margin-top: 20px;" 
        :data="selectedRefundOrder.items"
      >
        <el-table-column
          prop="brand_name"
          label="Brand Name"
        />
        <el-table-column
          prop="product_name"
          label="Product Name"
        />
        <el-table-column
          prop="size"
          label="Size"
        />
        <el-table-column
          prop="quantity"
          label="Quantity"
          width="100"
        />
        <el-table-column
          prop="refunded_qty"
          label="Refunded Quantity"
          width="100"
        />
        <el-table-column
          label="Refund Quantity"
          v-if="!isFullRefund"
        >
          <template slot-scope="scope">
            <el-select
              v-model="scope.row.refund_quantity"
              style="width:70px;"
            >
              <el-option
                key="0"
                label="0"
                value="0"
              />
              <el-option
                v-for="i in (scope.row.quantity - scope.row.refunded_qty)"
                :key="i"
                :label="i"
                :value="i"
              />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column
          prop="currency"
          label="Currency"
          width="100"
        />
        <el-table-column
          prop="unit_price"
          label="Unit Price"
          width="100"
        />
      </el-table>
      <div style="margin-top: 20px;text-align: right;">
        <el-button
          :loading="isSaving"
          class="save-button"
          size="mini"
          type="primary"
          @click="handleSubmitRefund"
        >{{ $t('button.submit') }}</el-button>
      </div>
      <!-- <el-button
          :disabled="isLoading"
          :loading="isSaving"
          class="save-button"
          size="mini"
          type="primary"
          @click="handleSave" -->
    </el-dialog>
  </sticky-header-layout>
</template>

<script>
import { createComponent, onBeforeMount, ref, reactive, computed } from '@vue/composition-api'

import ListPageHeader from '../layout/components/ListPageHeader.vue'

import PrivateClientsService from '../../api/private-clients'
import useTablePaging from '../../hooks/useTablePaging'
import { formatDatetime } from '../../utils/datetime'
import clipboard from '../../directive/clipboard'
import store from '../../store'

const ListHandler = (list) => {
  list.forEach((item, index, l) => {
    if (item.items) {
      for(let i of item.items) {
        i.refund_quantity = 0
      }
    }
    if (l[index].expired_at) {
      l[index].expired_at = formatDatetime({date: l[index].expired_at})
    }
  })
  return list
}

const PrivateClientOrderList = createComponent({
  name: 'PrivateClientOrderList',
  components: {
    ListPageHeader,
  },
  directives: {
    clipboard,
  },
  setup(props, { root }) {
    const { $router } = root

    const privateClientID = ref('')
    const isPageLoading = ref(false)
    const isPaid = ref(false)

    const showRefundModal = ref(false)
    const isFullRefund = ref(true)
    const refundTo = ref(null)
    const showOrderDetailModal = ref(false)
    const selectedOrder = reactive({})
    const selectedRefundOrder = reactive({})
    const canFullRefund = ref(true)
    const isSaving = ref(false)

    const {
      isLoading,
      list,
      listQuery,
      getList,
      handleSizeChange,
      handlePageChange,
      handleQueryParamsUpdate,
    } = useTablePaging({ service: PrivateClientsService.getOrderList, listHandler: ListHandler })

    const clientList = ref([])
    const getClientList = async () => {
      const { data } = await PrivateClientsService.getClientList()
      // [{ id: 1, name: "test"}, ...]
      clientList.value = data.map(({ id: value, name: label }) => ({
        label, value,
      }))
    }

    onBeforeMount(() => {
      getClientList()
      getList()
    })

    const handleClickCreateButton = () => {
      $router.push({ path: '/private-client/order/create' })
    }
    const handleClickEditButton = ({ id }) => {
      $router.push({ path: `/private-client/order/edit/${id}` })
    }

    const showOrderDetail = (record) => {
      // selectedOrder = record
      // console.log(selectedOrder)
      Object.assign(selectedOrder, record) // equivalent to reassign
      // selectedOrder.value = record
      isPaid.value = (record.payment_status === 1)

      root.$nextTick(() => {
        showOrderDetailModal.value = true
      })
    }
    const showRefundDetail = (record) => {
      // selectedRefundOrder = record
      Object.assign(selectedRefundOrder, record) // equivalent to reassign
      root.$nextTick(() => {
        showRefundModal.value = true
      })
    }
    const resetRefundParam = () => {
      checkCanFullRefund()
      isFullRefund.value = canFullRefund.value
      refundTo.value = null
      
      if (selectedRefundOrder.items) {
        for (let item of selectedRefundOrder.items) {
          item.refund_quantity = 0
        }
      }
    }
    const checkCanFullRefund = () => {
      canFullRefund.value = selectedRefundOrder.payment_status == 2
    }
    const handleRefundModalClosed = () => {
      Object.assign(selectedRefundOrder, {})
    }
    const handleSubmitRefund = () => {
      if (!refundFormValidate()) {
        return
      }

      root.$confirm('Confirm Refund?', root.$t('common.warning'), {
        confirmButtonText: root.$t('button.ok'),
        cancelButtonText: root.$t('button.cancel'),
        type: 'warning',
      }).then(() => {
        submitRefund()
      }).catch((error) => {
      })
    }
    const submitRefund = async() => {
      isSaving.value = true
      try {
        if (isFullRefund.value) {
          await submitFullRefund()
        } else {
          await submitPartialRefund()
        }

        root.$message({
          message: 'Refund Success',
          type: 'success',
        })
        isSaving.value = false
        showRefundModal.value = false
        getList()
      } catch (error) {
        isSaving.value = false
        root.$alert(`Error: ${error}`, root.$t('error.somethingWentWrong'), {
          type: 'error',
          confirmButtonText: root.$t('button.ok'),
        })
      }
    }
    const submitFullRefund = async() => {
      const form = {}
      form.refund_to = refundTo.value

      await PrivateClientsService.requestFullRefundOrder(form, selectedRefundOrder.id)
    }
    const submitPartialRefund = async() => {
      const form = {}
      let items = []
      for (let item of selectedRefundOrder.items) {
        if (item.refund_quantity > 0) {
          items.push({
            "id": item.id, 
            "refund_quantity": item.refund_quantity
          })
        }
      }

      form.items = items
      form.refund_to = refundTo.value

      await PrivateClientsService.requestPartialRefundOrder(form, selectedRefundOrder.id)
    }
    const refundFormValidate = () => {
      if (!refundTo.value) {
        root.$alert('Please select Refund to', root.$t('common.oops'), {
          confirmButtonText: root.$t('button.ok'),
        });
        return false
      }

      if (!isFullRefund.value) {
        let refundCount = 0
        for (let item of selectedRefundOrder.items) {
          refundCount += +item.refund_quantity
        }

        if (!refundCount) {
          root.$alert('No item selected', root.$t('common.oops'), {
            confirmButtonText: root.$t('button.ok'),
          });

          return false
        }
      }

      return true
    }
    const handleSearch = () => {
      handleQueryParamsUpdate({
        page: 1,
        private_client_id: privateClientID.value,
      })
      getList()
    }

    const isList = data => Array.isArray(data)

    const handleCopySuccess = () => {
      root.$message('Copied Link Successfully')
    }

    // const payLink = computed(() => `${process.env.VUE_APP_GOXIP_BASE_URL}/hk/en/order/${selectedOrder.hash}`)
    const getPayLink = (hash) => {
      return `${process.env.VUE_APP_GOXIP_BASE_URL}/hk/en/order/${hash}`
    }

    return {
      showOrderDetailModal,
      selectedOrder,
      showRefundModal,
      isFullRefund,
      refundTo,
      selectedRefundOrder,
      canFullRefund,
      clientList,

      isPaid,
      isSaving,
      isLoading,
      isPageLoading,
      privateClientID,
      list,
      listQuery,
      handleSizeChange,
      handlePageChange,

      handleClickCreateButton,
      handleClickEditButton,
      handleCopySuccess,
      showOrderDetail,
      showRefundDetail,
      resetRefundParam,
      handleRefundModalClosed,
      handleSubmitRefund,
      handleSearch,
      isList,
      getPayLink,
    }
  },
})

export default PrivateClientOrderList

</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";

::v-deep {
  .el-tag {
    white-space: normal;
    height: auto;
    word-break: break-word;
  }
  .el-table__row {
    .cell {
      a {
        text-decoration: underline;
      }
    }
  }
}
</style>
